import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BookingCodeForm, BookingDetails } from '../../types';
import { authService } from '../../services/authService';
import QRScanner from '../common/QRScanner';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { getCurrentHotelCode, clearHotelCode, storeHotelCode, getQrEntryHotelCode } from '../../utils/hotelContext';
import { readNextTarget, routeAfterAuth } from '../../utils/nextTarget';
import { useHotelBranding } from '../../hooks/useHotelBranding';
import { GDSpinnerScreen } from '../gd/GDBits';
import TurnstileWidget, { TurnstileHandle } from '../common/TurnstileWidget';

// Backend captcha / brute-force responses: 403 captcha_required |
// captcha_failed and 429 login_locked. Their `detail` is guest-facing.
const CAPTCHA_ERROR_CODES = ['captcha_required', 'captcha_failed', 'login_locked'];

function captchaErrorMessage(err: any): string | null {
  const body = err?.body || {};
  const code = err?.code || body.code;
  if (code && CAPTCHA_ERROR_CODES.includes(code)) {
    return body.detail || err?.error || null;
  }
  return null;
}

interface BookingCodeEntryProps {
  onAuthSuccess: (
    booking: BookingDetails,
    method: 'booking_code' | 'mobile_verification' | 'secure_token',
    additionalData?: {
      isCheckedIn?: boolean;
      checkInStatus?: string;
      canAccessServices?: boolean;
    }
  ) => void;
  isSecureToken?: boolean;
}

/**
 * Booking-code login in the standard gd layout: hotel-branded hero (when the
 * visitor arrived via a room/reception QR), "explore the hotel" shortcut,
 * booking-code form and QR scan. All auth logic is unchanged — only the
 * presentation moved from Chakra to the gd design system.
 */
const BookingCodeEntry: React.FC<BookingCodeEntryProps> = ({ onAuthSuccess, isSecureToken = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bookingCode, token } = useParams<{ bookingCode?: string; token?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  // Room-number check-out verification (per-hotel alternative to the
  // booking code — see admine → Guest App → Self check-out).
  const [roomNumber, setRoomNumber] = useState('');
  const [roomSecret, setRoomSecret] = useState('');
  // Guest-name fallback (QR visits only): OTA guests often hold a
  // different booking number from the one the channel sent us.
  const [nameMode, setNameMode] = useState(false);
  const [guestName, setGuestName] = useState('');
  // Cloudflare Turnstile (manual forms only). The backend refuses these
  // forms without a valid token, so submit stays disabled until the
  // widget yields one; the secure-link path never shows a form.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const queryClient = useQueryClient();

  // A hotel code means this visitor arrived via a room/reception QR code —
  // brand the landing page for that hotel and surface its info directory
  // before asking for a booking code. The shared branding hook resolves the
  // code, caches the fetch under the same key every other screen uses, and
  // applies the hotel's accent/header colors.
  const hotelCode = getCurrentHotelCode();
  // Reception QR codes land here with ?next=checkin|checkout: the screen
  // titles itself for that flow, always asks for a booking code (any
  // existing session is wiped below), and sends the guest straight in.
  const nextTarget = readNextTarget();
  // Offer the name fallback only when this session was opened from THIS
  // hotel's QR code — never on emailed links or a plain visit.
  const qrEntryHotel = getQrEntryHotelCode();
  const canUseName =
    !!hotelCode && !!qrEntryHotel && qrEntryHotel.toUpperCase() === hotelCode.toUpperCase();
  const { hotelInfo: hotelHero, logoSrc, query: heroQuery } = useHotelBranding();
  const isHeroLoading = heroQuery.isLoading;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<BookingCodeForm>();

  // Auto-fill booking code from URL if present
  useEffect(() => {
    // /booking/:token also lands here when the token is too short to be a
    // secure token — treat it as a booking code so the link still works.
    const codeFromUrl = bookingCode || (!isSecureToken ? token : undefined);
    if (isSecureToken && token) {
      // A guest link is authoritative: wipe any previous session (another
      // booking's tokens, cached bill/profile queries, the stored hotel
      // code from an earlier stay) BEFORE authenticating, so stale data
      // can never bleed into this stay. The new booking's hotel code is
      // re-stored on auth success.
      authService.clearAuth();
      clearHotelCode();
      queryClient.clear();
      // Handle secure token authentication
      handleSecureTokenAuth(token);
    } else if (codeFromUrl) {
      authService.clearAuth();
      clearHotelCode();
      queryClient.clear();
      setValue('booking_code', codeFromUrl);
      // Auto-submit if code is provided in URL
      handleSubmit(onSubmit)();
    } else if (nextTarget) {
      // Reception QR scan: start from a clean slate so the desk's shared
      // printed code can never hand one guest another's stay. The hotel
      // code from the QR's ?h= is re-stored right after.
      const qrHotelCode = new URLSearchParams(window.location.search).get('h');
      authService.clearAuth();
      clearHotelCode();
      queryClient.clear();
      if (qrHotelCode) storeHotelCode(qrHotelCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCode, token, isSecureToken, setValue, handleSubmit]);

  const onSubmit = async (data: BookingCodeForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.authenticateWithBookingCode({
        // Send the code as typed (trimmed). 158 stored OTA references
        // legitimately contain dashes, so the app must NOT strip
        // separators — the backend tries the exact code first, then a
        // decoration-stripped variant, and matches either.
        booking_code: data.booking_code.toUpperCase().trim(),
        cf_turnstile_token: captchaToken,
      });

      if (response.success) {
        onAuthSuccess(response.data.booking, 'booking_code', {
          isCheckedIn: response.data.is_checked_in || false,
          checkInStatus: response.data.check_in_status || 'unknown',
          canAccessServices: true
        });
        // Reception QR codes carry ?next=checkin|checkout — land the
        // guest on that flow instead of the generic home screen.
        navigate(routeAfterAuth());
      }
    } catch (err: any) {
      // Turnstile tokens are single-use — get a fresh one for the retry.
      turnstileRef.current?.reset();
      const captchaMsg = captchaErrorMessage(err);
      if (captchaMsg) {
        setError(captchaMsg);
      } else if (err.check_in_required) {
        setError(`${err.error} ${t('auth.bookingCode.errors.checkInRequired')}`);
      } else {
        setError(err.error || t('auth.bookingCode.errors.invalid'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecureTokenAuth = async (secureToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.authenticateWithSecureToken(secureToken);

      if (response.success) {
        onAuthSuccess(response.data.booking, 'secure_token', {
          isCheckedIn: response.data.is_checked_in,
          checkInStatus: response.data.check_in_status,
          canAccessServices: response.data.can_access_services
        });
        // ?next=checkin/checkout on the link lands the guest directly on
        // that flow (state-aware links from emails / the staff button).
        navigate(routeAfterAuth());
      }
    } catch (err: any) {
      if (err.error_code === 'link_expired') {
        // Links die 24h after checkout — land on the hotel's public info
        // page (branded via the token's hotel) with the booking-code
        // form one tap away, instead of a dead-end error screen.
        if (err.hotel?.code) storeHotelCode(err.hotel.code);
        navigate('/hotel-info?linkExpired=1', { replace: true });
        return;
      }
      if (err.check_in_required) {
        setError(`${err.error} ${t('auth.bookingCode.errors.checkInRequired')}`);
      } else {
        setError(err.error || t('auth.bookingCode.errors.secureTokenInvalid'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitRoomVerify = async () => {
    const room = roomNumber.trim();
    if (!room || !hotelCode) return;
    setIsLoading(true);
    setError(null);
    try {
      const response: any = await authService.verifyCheckout({
        hotel_code: hotelCode,
        room_number: room,
        ...(needsSecret ? { value: roomSecret.trim() } : {}),
        cf_turnstile_token: captchaToken,
      });
      if (response.success) {
        storeHotelCode(hotelCode);
        onAuthSuccess(response.data.booking, 'booking_code', {
          isCheckedIn: response.data.is_checked_in,
          checkInStatus: response.data.check_in_status,
          canAccessServices: response.data.can_access_services,
        });
        navigate('/checkout');
      }
    } catch (err: any) {
      turnstileRef.current?.reset();
      setError(captchaErrorMessage(err) || err?.error || t('auth.bookingCode.errors.invalid'));
    } finally {
      setIsLoading(false);
    }
  };

  const submitGuestName = async () => {
    const name = guestName.trim();
    if (!name || !hotelCode) return;
    setIsLoading(true);
    setError(null);
    try {
      const response: any = await authService.authenticateWithGuestName({
        hotel_code: hotelCode,
        guest_name: name,
        cf_turnstile_token: captchaToken,
      });
      if (response.success) {
        storeHotelCode(hotelCode);
        onAuthSuccess(response.data.booking, 'booking_code', {
          isCheckedIn: response.data.is_checked_in,
          checkInStatus: response.data.check_in_status,
          canAccessServices: response.data.can_access_services,
        });
        navigate(routeAfterAuth());
      }
    } catch (err: any) {
      turnstileRef.current?.reset();
      setError(captchaErrorMessage(err) || err?.error || t('auth.bookingCode.errors.invalid'));
    } finally {
      setIsLoading(false);
    }
  };

  const switchToName = (on: boolean) => {
    setNameMode(on);
    setError(null);
    // Each mode mounts its own Turnstile widget, which issues a new token.
    setCaptchaToken(null);
  };

  const handleQRScan = (result: string) => {
    // Extract booking code from QR result
    const code = result.split('/').pop() || result;
    setValue('booking_code', code);
    setShowQRScanner(false);
    handleSubmit(onSubmit)();
  };

  // Secure-token links authenticate on mount — show progress, not a form.
  if (isSecureToken && isLoading && !error) {
    return <GDSpinnerScreen label={t('auth.bookingCode.loadingText')} />;
  }

  const hotel = hotelHero?.hotel;
  const hotelName = hotel?.name;

  // Check-out can be verified by room number (optionally plus a
  // room-wise custom value) instead of the booking code. Only applies
  // to the check-out entry point; check-in always uses the code.
  const verifyConfig = (hotel as any)?.checkout_verification;
  const verifyMethod = verifyConfig?.method || 'booking_code';
  const roomVerify =
    nextTarget === 'checkout' && verifyMethod !== 'booking_code' && !!hotelCode;
  const needsSecret = verifyMethod === 'room_number_custom';
  const secretLabel =
    (verifyConfig?.label && String(verifyConfig.label).replace(/\b\w/g, (c: string) => c.toUpperCase()))
    || t('auth.bookingCode.verifyValueLabel', 'Verification code');

  return (
    <section className="gd-screen">
      <div className="gd-hero">
        <div className="gd-row" style={{ marginBottom: 10 }}>
          <span />
          <LanguageSwitcher compact />
        </div>
        <div className="gd-hotel">
          <div className="gd-logo">
            {logoSrc ? <img src={logoSrc} alt={hotelName || ''} /> : (hotelName || 'F').charAt(0)}
          </div>
          <div>
            <b>{isHeroLoading ? '…' : hotelName || 'FrontDesko'}</b>
            <span>
              {hotel
                ? [hotel.city, hotel.country].filter(Boolean).join(' · ')
                : t('auth.bookingCode.subtitle')}
            </span>
          </div>
          {hotel?.phone && (
            <a className="gd-call" href={`tel:${hotel.phone}`}>📞 {hotel.phone}</a>
          )}
        </div>
        <h1>
          {nextTarget === 'checkin'
            ? t('auth.bookingCode.checkinTitle', 'Check in')
            : nextTarget === 'checkout'
              ? t('auth.bookingCode.checkoutTitle', 'Check out')
              : hotelCode && hotelName ? hotelName : t('auth.bookingCode.title')}
        </h1>
        <p>
          {nextTarget === 'checkin'
            ? t('auth.bookingCode.checkinSubtitle', 'Enter your booking code to start check-in.')
            : nextTarget === 'checkout'
              ? roomVerify
                ? t('auth.bookingCode.checkoutRoomSubtitle', 'Enter your room number to start check-out.')
                : t('auth.bookingCode.checkoutSubtitle', 'Enter your booking code to start check-out.')
              : hotelCode && hotel?.description
                ? hotel.description
                : t('auth.bookingCode.subtitle')}
        </p>
      </div>

      {roomVerify ? (
        <div className="gd-body">
          {error && <div className="gd-note" role="alert">{error}</div>}
          <form
            onSubmit={(e) => { e.preventDefault(); submitRoomVerify(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div className="gd-field">
              <label htmlFor="room_number">{t('auth.bookingCode.roomLabel', 'Room number')}</label>
              <input
                id="room_number"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                placeholder={t('auth.bookingCode.roomPlaceholder', 'e.g. 101') as string}
                autoComplete="off"
                autoFocus
              />
            </div>
            {needsSecret && (
              <div className="gd-field">
                <label htmlFor="verify_value">{secretLabel}</label>
                <input
                  id="verify_value"
                  value={roomSecret}
                  onChange={(e) => setRoomSecret(e.target.value)}
                  autoComplete="off"
                />
              </div>
            )}
            <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />
            <button type="submit" className="gd-btn" disabled={isLoading || !roomNumber.trim() || !captchaToken}>
              {isLoading
                ? t('auth.bookingCode.verifying', 'Checking…')
                : t('auth.bookingCode.continue', 'Continue')}
            </button>
          </form>
        </div>
      ) : nameMode && canUseName ? (
        <div className="gd-body">
          <p className="gd-muted">
            {t('auth.bookingCode.nameSubtitle', 'Enter your full name exactly as it appears on your booking.')}
          </p>
          {error && (
            <div className="gd-note" role="alert" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
              {error}
            </div>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); submitGuestName(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div className="gd-field">
              <label htmlFor="guest_name">{t('auth.bookingCode.nameLabel', 'Full name (as on your booking)')}</label>
              <input
                id="guest_name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder={t('auth.bookingCode.namePlaceholder', 'e.g. Jane Doe') as string}
                autoComplete="name"
                autoFocus
              />
            </div>
            <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />
            <button type="submit" className="gd-btn" disabled={isLoading || !guestName.trim() || !captchaToken}>
              {isLoading
                ? t('auth.bookingCode.verifying', 'Checking…')
                : t('auth.bookingCode.continue', 'Continue')}
            </button>
          </form>
          <button type="button" className="gd-btn gd-ghost" onClick={() => switchToName(false)}>
            {t('auth.bookingCode.useBookingCode', 'Use booking number instead')}
          </button>
        </div>
      ) : (
      <div className="gd-body">
        {hotelCode && !nextTarget && (
          <button type="button" className="gd-tile gd-wide" onClick={() => navigate('/hotel-info')}>
            <div className="gd-ico">ℹ️</div>
            <div>
              <b>{t('hotelInfo.cardTitle')}</b>
              <small>{t('hotelInfo.cardDescription')}</small>
            </div>
          </button>
        )}

        <p className="gd-muted" style={{ fontWeight: 700, color: 'var(--gd-ink-2)' }}>
          {t('hotelInfo.haveBooking')}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="gd-field">
            <label htmlFor="booking_code">{t('auth.bookingCode.fieldLabel')}</label>
            <input
              id="booking_code"
              placeholder={t('auth.bookingCode.fieldPlaceholder')}
              autoComplete="off"
              autoCapitalize="characters"
              {...register('booking_code', {
                required: t('auth.bookingCode.errors.required') as string,
                minLength: {
                  value: 3,
                  message: t('auth.bookingCode.errors.minLength') as string,
                },
                pattern: {
                  // Guests paste booking numbers exactly as their OTA
                  // app formats them — "1780 361 151", "1780-361-151",
                  // "#1780361151". Accept the decoration here; it is
                  // stripped before the lookup (and the backend strips
                  // it again). Rejecting a pasted space showed the same
                  // "letters and numbers only" error a genuinely bad
                  // code gets — guests read it as "my booking ID is
                  // wrong" (TBH Agoda guests, 2026-10-03).
                  value: /^#?[A-Za-z0-9](?:[A-Za-z0-9 -]*[A-Za-z0-9])?$/,
                  message: t('auth.bookingCode.errors.pattern') as string,
                },
              })}
            />
          </div>
          {errors.booking_code && (
            <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
              {errors.booking_code.message}
            </div>
          )}
          {error && (
            <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
              {error}
            </div>
          )}
          <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />
          <button type="submit" className="gd-btn" disabled={isLoading || !captchaToken}>
            {isLoading ? t('auth.bookingCode.loadingText') : t('auth.bookingCode.submitButton')}
          </button>
        </form>

        {canUseName && (
          <button type="button" className="gd-btn gd-ghost" onClick={() => switchToName(true)}>
            {t('auth.bookingCode.useNameInstead', 'Booking number not working? Use your name')}
          </button>
        )}

        <button type="button" className="gd-btn gd-ghost" onClick={() => setShowQRScanner(!showQRScanner)}>
          {t('auth.bookingCode.scanQR')}
        </button>

        <div className="gd-powered">
          {t('gd.home.poweredBy')} <b>FrontDesko</b>
        </div>
      </div>
      )}

      {/* Real camera scanner — same component the dashboard uses */}
      <QRScanner
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={handleQRScan}
      />
    </section>
  );
};

export default BookingCodeEntry;
